import { useQuery, useSubscription } from '@apollo/client/react';
import { useCallback, useMemo } from 'react';
import {
  GET_PERSON_DATA,
  GET_FAMILY_MEMBERS_DATA,
  SUBSCRIBE_PERSON_DATA,
} from '@common/graphql';
import {
  insertPersonData as apiInsertPersonData,
  updateData,
  deletePersonData as apiDeletePersonData,
  insertSharedPersonData as apiInsertSharedPersonData,
} from '../services/dataApi';

// Data types for person-specific data (tied to individuals, not families)
export type PersonDataType = 'illness' | 'allergy' | 'medication' | 'activity' | 'class' | 'clothing_sizes' | 'document' | 'chore' | 'routine' | 'assignment';

// The data structure stored in person_data table
// Now uses nested data relationship (similar to family_data)
export interface PersonDataItem<T = any> {
  id: string;
  person_id: string;
  data: {
    id: string;
    type: PersonDataType;
    data: T;
  };
  status: string;
  created_at: string;
  modified_at: string;
}

interface UsePersonDataOptions {
  skip?: boolean;
  useSubscription?: boolean;
}

interface UsePersonDataResult<T> {
  items: PersonDataItem<T>[];
  loading: boolean;
  error: any;
  refetch: () => Promise<any>;
  insert: (data: T) => Promise<PersonDataItem<T> | null>;
  update: (id: string, data: T) => Promise<PersonDataItem<T> | null>;
  remove: (id: string) => Promise<boolean>;
}

/**
 * Hook to manage person-specific data
 * Used for health data (medications, illnesses, allergies) tied to individuals
 */
export function usePersonData<T = any>(
  person_id: string | undefined,
  type: PersonDataType,
  options?: UsePersonDataOptions
): UsePersonDataResult<T> {
  const useRealtime = options?.useSubscription !== false;

  // Use subscription for real-time updates
  const {
    data: subscriptionData,
    loading: subLoading,
    error: subError,
  } = useSubscription(SUBSCRIBE_PERSON_DATA, {
    variables: { person_id },
    skip: options?.skip || !person_id || !useRealtime,
  });

  // Query — always active as a refetchable fallback
  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(GET_PERSON_DATA, {
    variables: { person_id },
    skip: options?.skip || !person_id,
    fetchPolicy: 'cache-and-network',
  });

  // Prefer subscription data when available
  const data = useRealtime && (subscriptionData as any)?.person_data ? subscriptionData : queryData;
  const loading = useRealtime ? subLoading && queryLoading : queryLoading;
  const error = (data as any)?.person_data ? null : subError || queryError;

  // Filter items by type using the nested data.type field
  const items = useMemo(() => {
    if (!(data as any)?.person_data) return [];
    return (data as any).person_data.filter((item: PersonDataItem<T>) => item.data?.type === type);
  }, [data, type]);

  // Insert new data
  const insert = useCallback(
    async (itemData: T): Promise<PersonDataItem<T> | null> => {
      if (!person_id) {
        console.error('No person_id provided to insert data');
        return null;
      }

      try {
        const result = await apiInsertPersonData(person_id, type, itemData);
        return result || null;
      } catch (err) {
        console.error('Error inserting person data:', err);
        throw err;
      }
    },
    [person_id, type]
  );

  // Update existing data (updates the data table directly)
  const update = useCallback(
    async (id: string, itemData: T): Promise<PersonDataItem<T> | null> => {
      try {
        const result = await updateData(id, type, itemData);
        return result || null;
      } catch (err) {
        console.error('Error updating person data:', err);
        throw err;
      }
    },
    [type]
  );

  // Soft delete data
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await apiDeletePersonData(id);
        return true;
      } catch (err) {
        console.error('Error deleting person data:', err);
        throw err;
      }
    },
    []
  );

  return {
    items,
    loading,
    error,
    refetch,
    insert,
    update,
    remove,
  };
}

/**
 * Hook to fetch person data for multiple family members
 * Useful for displaying health overview for the whole family
 */
export function useFamilyMembersData<T = any>(
  person_ids: string[],
  type: PersonDataType,
  options?: UsePersonDataOptions
) {
  const { data, loading, error, refetch } = useQuery(GET_FAMILY_MEMBERS_DATA, {
    variables: { person_ids },
    skip: options?.skip || person_ids.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  // Filter items by type client-side
  const items = useMemo(() => {
    if (!(data as any)?.person_data) return [];
    return (data as any).person_data.filter(
      (item: PersonDataItem<T>) => item.data?.type === type
    );
  }, [data, type]);

  return {
    items,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for inserting shared data linked to multiple people
 * Used for activities, classes, etc. where multiple people share the same data
 */
export function useSharedPersonData<T = any>(type: PersonDataType) {
  // Insert one data record and link it to multiple people (single transaction)
  const insertForMultiplePeople = useCallback(
    async (itemData: T, personIds: string[]): Promise<string | null> => {
      if (personIds.length === 0) {
        console.error('No person_ids provided');
        return null;
      }

      try {
        const result = await apiInsertSharedPersonData(type, itemData, personIds);
        return result?.id || null;
      } catch (err) {
        console.error('Error inserting shared person data:', err);
        throw err;
      }
    },
    [type]
  );

  return {
    insertForMultiplePeople,
  };
}

export default usePersonData;
